import { useState } from 'react'
import Boton from '../components/Boton'
import Revelar from '../components/Revelar'
import { useContacto } from '../hooks/useContacto'
import { contacto } from '../data/contenido'

const CAMPOS_VACIOS = { nombre: '', email: '', mensaje: '' }

// Mismos límites que aplica la Cloud Function, para que el error salga aquí
// antes de gastar una invocación.
function validar({ nombre, email, mensaje }) {
  const errores = {}
  if (!nombre.trim()) errores.nombre = 'Escribe tu nombre.'
  else if (nombre.trim().length > 100) errores.nombre = 'Máximo 100 caracteres.'

  if (!email.trim()) errores.email = 'Escribe tu correo.'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
    errores.email = 'Ese correo no parece válido.'

  if (!mensaje.trim()) errores.mensaje = 'Cuéntanos qué necesitas.'
  else if (mensaje.trim().length > 2000)
    errores.mensaje = 'Máximo 2000 caracteres.'

  return errores
}

function Campo({ id, etiqueta, error, children }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs uppercase tracking-[0.2em] text-marfil-suave"
      >
        {etiqueta}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} className="mt-2 text-xs text-oro-claro">
          {error}
        </p>
      )}
    </div>
  )
}

// text-base en móvil (16px) es obligatorio: por debajo de eso Safari en iOS
// hace zoom automático al enfocar el campo y deja la página descuadrada.
// Desde sm se recupera el text-sm del diseño.
const claseCampo =
  'mt-2 w-full rounded-sm border border-oro-profundo/40 bg-noche px-4 py-3 text-base text-marfil placeholder:text-marfil-suave/40 transition-colors focus:border-oro focus:outline-none sm:text-sm'

function Contacto() {
  const { enviar, estado, error: errorEnvio } = useContacto()
  const [valores, setValores] = useState(CAMPOS_VACIOS)
  const [errores, setErrores] = useState({})

  const cambiar = (campo) => (evento) => {
    setValores((v) => ({ ...v, [campo]: evento.target.value }))
    setErrores((e) => ({ ...e, [campo]: undefined }))
  }

  const alEnviar = async (evento) => {
    evento.preventDefault()

    const encontrados = validar(valores)
    setErrores(encontrados)
    if (Object.keys(encontrados).length > 0) return

    const ok = await enviar(valores)
    if (ok) setValores(CAMPOS_VACIOS)
  }

  return (
    <section
      id="contacto"
      aria-labelledby="titulo-contacto"
      className="seccion-y border-t border-oro-profundo/20"
    >
      <div className="contenedor grid gap-10 sm:gap-12 lg:grid-cols-2 lg:gap-20">
        <Revelar>
          <p className="text-[0.7rem] uppercase tracking-[0.3em] text-oro sm:text-xs sm:tracking-[0.35em]">
            Contacto
          </p>
          <h2 id="titulo-contacto" className="mt-4 text-seccion font-light">
            {contacto.titulo}
          </h2>
          <p className="mt-4 max-w-md text-entradilla text-marfil-suave/85">
            {contacto.entradilla}
          </p>
        </Revelar>

        <Revelar retraso={0.1}>
          {estado === 'enviado' ? (
            <div
              role="status"
              className="rounded-sm border border-oro-profundo bg-carbon p-6 sm:p-8"
            >
              <p className="font-display text-xl text-oro-claro">
                Mensaje recibido
              </p>
              <p className="mt-3 text-sm text-marfil-suave/85">
                Te respondemos pronto. Si es urgente, escríbenos por WhatsApp
                desde la ficha de cualquier gatico disponible.
              </p>
            </div>
          ) : (
            <form onSubmit={alEnviar} noValidate className="space-y-6">
              <Campo id="nombre" etiqueta="Nombre" error={errores.nombre}>
                <input
                  id="nombre"
                  name="nombre"
                  type="text"
                  autoComplete="name"
                  maxLength={100}
                  value={valores.nombre}
                  onChange={cambiar('nombre')}
                  aria-invalid={Boolean(errores.nombre)}
                  aria-describedby={errores.nombre ? 'nombre-error' : undefined}
                  className={claseCampo}
                />
              </Campo>

              <Campo id="email" etiqueta="Correo" error={errores.email}>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  maxLength={254}
                  value={valores.email}
                  onChange={cambiar('email')}
                  aria-invalid={Boolean(errores.email)}
                  aria-describedby={errores.email ? 'email-error' : undefined}
                  className={claseCampo}
                />
              </Campo>

              <Campo id="mensaje" etiqueta="Mensaje" error={errores.mensaje}>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  rows={5}
                  maxLength={2000}
                  value={valores.mensaje}
                  onChange={cambiar('mensaje')}
                  aria-invalid={Boolean(errores.mensaje)}
                  aria-describedby={
                    errores.mensaje ? 'mensaje-error' : undefined
                  }
                  className={`${claseCampo} resize-y`}
                />
              </Campo>

              {errorEnvio && (
                <p
                  role="alert"
                  className="rounded-sm border border-oro-profundo bg-leopardo px-4 py-3 text-sm text-marfil"
                >
                  {errorEnvio}
                </p>
              )}

              <Boton
                type="submit"
                disabled={estado === 'enviando'}
                className="w-full xs:w-auto"
              >
                {estado === 'enviando' ? 'Enviando…' : 'Enviar mensaje'}
              </Boton>
            </form>
          )}
        </Revelar>
      </div>
    </section>
  )
}

export default Contacto
